import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

interface Report {
  project: {
    id: number
    name: string
    customer: { id: number; name: string }
    project_type: string
    hour_cap: number | null
  }
  window: { start_date: string; end_date: string }
  revenue: { raw: number; after_discounts: number; discount_total: number }
  time: { total_hours: number; billable_hours: number; entry_count: number }
  cost: { planned: number; actual: number }
  margin: {
    planned_profit: number
    planned_margin_percentage: number | null
    actual_profit: number
    actual_margin_percentage: number | null
  }
  headcount: number
  team: Array<{
    user: { id: number; name: string }
    stint: { started_on: string; ended_on: string; open: boolean }
    hours: number
    revenue: number
    planned_cost: number
    planned_margin: number
  }>
  pricing_changes: Array<{
    kind: string
    effective_date: string
    summary: string
    change_reason: string | null
  }>
  team_movement: Array<{
    kind: string
    date: string
    user: string
    close_reason?: string | null
  }>
  data_quality: { unbilled_time_entries: number; capped_at_hour_limit: boolean }
}

interface Props {
  [key: string]: unknown
  filters: { start_date: string; end_date: string }
  report: Report
}

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" })

function marginToneClass(pct: number | null): string {
  if (pct == null) return "text-muted-foreground"
  if (pct < 0) return "text-red-600 dark:text-red-400"
  if (pct < 20) return "text-amber-600 dark:text-amber-400"
  if (pct < 40) return ""
  if (pct < 60) return "text-emerald-600 dark:text-emerald-400"
  return "text-emerald-700 dark:text-emerald-300"
}

export default function ProjectFinanceReportShow() {
  const { filters, report } = usePage<Props>().props
  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)

  function applyFilters() {
    router.get(
      `/admin/project_finance_reports/${report.project.id}`,
      { start_date: startDate, end_date: endDate },
      { preserveScroll: true },
    )
  }

  return (
    <>
      <Head title={`${report.project.name} P&L - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/project_finance_reports">Project P&amp;L</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{report.project.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{report.project.name}</h1>
            <p className="text-muted-foreground text-sm">
              {report.project.customer.name} ·{" "}
              {report.project.project_type === "fixed_price" ? "Fixed Price" : "Time &amp; Materials"}
              {report.project.hour_cap != null && ` · cap ${report.project.hour_cap}h`}
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label>From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button onClick={applyFilters}>Apply</Button>
          </div>
        </div>

        {(() => {
          const totalCosts = report.cost.planned // Total Costs == Total Labor Cost (until non-labor project costs are tracked)
          const grossProfit = report.revenue.after_discounts - totalCosts
          const grossMargin =
            report.revenue.after_discounts > 0 && totalCosts > 0
              ? Math.round((grossProfit / report.revenue.after_discounts) * 1000) / 10
              : null
          return (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <SummaryCard label="Total Revenue ($)" value={fmt(report.revenue.after_discounts)} />
              <SummaryCard label="Total Costs ($)" value={fmt(totalCosts)} />
              <SummaryCard
                label="Gross Profit ($)"
                value={fmt(grossProfit)}
                tone={grossProfit >= 0 ? "positive" : "negative"}
              />
              <SummaryCard
                label="Gross Margin %"
                value={grossMargin != null ? `${grossMargin}%` : "—"}
                customToneClass={marginToneClass(grossMargin)}
              />
              <SummaryCard label="Headcount" value={String(report.headcount)} />
              <SummaryCard label="Total Labor Cost ($)" value={fmt(report.cost.planned)} />
            </div>
          )
        })()}

        {report.revenue.discount_total > 0 && (
          <p className="text-xs text-muted-foreground -mt-2">
            Revenue includes {fmt(report.revenue.discount_total)} of discounts (gross {fmt(report.revenue.raw)}).
          </p>
        )}

        {(report.data_quality.unbilled_time_entries > 0 || report.data_quality.capped_at_hour_limit) && (
          <Card className="border-orange-500/50">
            <CardHeader>
              <CardTitle className="text-sm">Data quality</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm text-muted-foreground">
              {report.data_quality.unbilled_time_entries > 0 && (
                <p>
                  {report.data_quality.unbilled_time_entries} time entries fall outside any active assignment window
                  (revenue not booked).
                </p>
              )}
              {report.data_quality.capped_at_hour_limit && (
                <p>Time logged exceeded the project's hour cap; revenue capped accordingly.</p>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Team breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Planned cost</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.team.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                      No active assignments in this window.
                    </TableCell>
                  </TableRow>
                ) : (
                  report.team.map((row, i) => (
                    <TableRow key={`${row.user.id}-${row.stint.started_on}-${i}`}>
                      <TableCell className="font-medium">{row.user.name}</TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {row.stint.started_on} – {row.stint.open ? "present" : row.stint.ended_on}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.hours}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(row.revenue)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(row.planned_cost)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(row.planned_margin)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {report.pricing_changes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Pricing changes in window</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Effective</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.pricing_changes.map((c, i) => (
                    <TableRow key={`${c.kind}-${c.effective_date}-${i}`}>
                      <TableCell className="tabular-nums">{c.effective_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {c.kind.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>{c.summary}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{c.change_reason ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {report.team_movement.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Team movement in window</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.team_movement.map((m, i) => (
                    <TableRow key={`${m.date}-${m.user}-${i}`}>
                      <TableCell className="tabular-nums">{m.date}</TableCell>
                      <TableCell>
                        <Badge variant={m.kind === "started" ? "default" : "outline"} className="capitalize">
                          {m.kind}
                        </Badge>
                      </TableCell>
                      <TableCell>{m.user}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{m.close_reason ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

function SummaryCard({
  label,
  value,
  tone,
  customToneClass,
}: {
  label: string
  value: string
  tone?: "positive" | "negative"
  customToneClass?: string
}) {
  const toneClass =
    customToneClass ??
    (tone === "positive" ? "text-emerald-600 dark:text-emerald-400" : tone === "negative" ? "text-destructive" : "")
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

ProjectFinanceReportShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
